import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, onSnapshot, query as fsQuery, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { PageTitle } from '../../../_metronic/layout/core';
import { useAppData } from '../../providers/AppDataProvider';
import { Customer } from '../customers/CustomerInterfaces';
import { ClientsFilters } from './components/ClientsFilters';
import { ClientsList } from './components/ClientsList';
import { ClientDetailPanel } from './components/ClientDetailPanel';
import NewCustomerModal from '../customers/NewCustomerModal';
import { CONTACT_TYPE_SORT_ORDER } from '../../constants/contactTypes';
import type { ContactTypeFilter } from '../../constants/contactTypes';
import './ClientsPage.scss';

const contactsBreadCrumbs = [
  { title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
];

// Re-exported from the shared constants module so existing import
// sites (ClientsFilters etc.) don't need to update their import path.
// New code should prefer importing ContactTypeFilter directly from
// '../../constants/contactTypes'.
export type { ContactTypeFilter } from '../../constants/contactTypes';
export type SortField = 'lastContacted' | 'company' | 'name' | 'createdAt' | 'contactType' | 'revenue' | 'recurring';
export type SortDirection = 'asc' | 'desc';

export interface ClientsFiltersState {
  search: string;
  contactType: ContactTypeFilter;
  sortField: SortField;
  sortDirection: SortDirection;
  hasActiveJobs: boolean | null;
  hasOutstandingInvoices: boolean | null;
}

const defaultFilters: ClientsFiltersState = {
  search: '',
  contactType: 'all',
  sortField: 'lastContacted',
  sortDirection: 'desc',
  hasActiveJobs: null,
  hasOutstandingInvoices: null,
};

export function ClientsPage() {
  const { customers, jobs, invoices, customersLoading } = useAppData();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [filters, setFilters] = useState<ClientsFiltersState>(defaultFilters);
  const [selectedContact, setSelectedContact] = useState<Customer | null>(null);
  const [showNewContactModal, setShowNewContactModal] = useState(false);

  // Deep-link: auto-select customer from ?id= query param
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam && customers.length > 0 && !customersLoading) {
      const match = customers.find(c => c.id === idParam);
      if (match) {
        setSelectedContact(match);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, customers, customersLoading, setSearchParams]);

  // Keep selectedContact in sync with updated customer data from Firestore
  useEffect(() => {
    if (selectedContact && selectedContact.id) {
      const updatedContact = customers.find(c => c.id === selectedContact.id);
      if (updatedContact) {
        if (JSON.stringify(updatedContact) !== JSON.stringify(selectedContact)) {
          setSelectedContact(updatedContact);
        }
      } else if (!customersLoading) {
        setSelectedContact(null);
      }
    }
  }, [customers, selectedContact, customersLoading]);

  // Calculate contact statistics
  // Get contact revenue data.
  //
  // 2026-05-13 fix: same Xero-schema crash as the contactStats useMemo
  // (invoice.Contact.ContactID was the original blank-page culprit).
  // This callback is passed to ClientsSummaryCards, ClientsList, AND
  // ClientDetailPanel as a prop, so any single native invoice in the
  // dataset crashed all three subtrees. Now reads native field names
  // with Xero-shape fallback.
  const getContactRevenue = useCallback((customerId: string) => {
    let total = 0;
    let outstanding = 0;
    let paid = 0;

    invoices.forEach((invoice: any) => {
      const cid = invoice?.contactId || invoice?.Contact?.ContactID || null;
      if (cid !== customerId) return;
      const status = String(invoice?.status || invoice?.Status || '').toUpperCase();
      if (status === 'VOIDED' || status === 'DRAFT') return;
      const inv_total = Number(invoice?.total ?? invoice?.Total) || 0;
      const inv_due = Number(invoice?.amountDue ?? invoice?.AmountDue) || 0;
      total += inv_total;
      if (status === 'PAID') {
        paid += inv_total;
      } else if (status === 'SENT' || status === 'APPROVED' || status === 'AUTHORISED') {
        outstanding += inv_due;
      }
    });

    return { total, outstanding, paid };
  }, [invoices]);

  // Get contact job counts. Defensive against legacy job docs where
  // customerId might be a string instead of an array — Array.isArray
  // guard prevents `.includes is not a function` crashes.
  const getContactJobCounts = useCallback((customerId: string) => {
    let active = 0;
    let completed = 0;
    let total = 0;

    jobs.forEach((job: any) => {
      const cids: string[] = Array.isArray(job?.customerId)
        ? job.customerId
        : (typeof job?.customerId === 'string' ? [job.customerId] : []);
      if (!cids.includes(customerId)) return;
      total++;
      if (['Open', 'Active', 'Pending', 'Awaiting Client Response'].includes(job.status)) {
        active++;
      } else if (job.status === 'Closed') {
        completed++;
      }
    });

    return { active, completed, total };
  }, [jobs]);

  // Live map of contactId → active-recurring-template count. One collection
  // subscription powers BOTH the per-row badge (ClientsList) and the
  // "Recurring" sort below — kept here so the sort can read it.
  const [recurringByContact, setRecurringByContact] = useState<Record<string, number>>({});
  useEffect(() => {
    const unsub = onSnapshot(
      fsQuery(collection(db, 'repeating_invoices'), where('status', '==', 'ACTIVE')),
      (snap) => {
        const counts: Record<string, number> = {};
        snap.forEach((d) => {
          const cid = (d.data() as any)?.contactId;
          if (cid) counts[cid] = (counts[cid] || 0) + 1;
        });
        setRecurringByContact(counts);
      },
      (err) => { console.warn('[ClientsPage] recurring sub error:', err); setRecurringByContact({}); },
    );
    return () => unsub();
  }, []);

  // Filter and sort contacts
  const filteredContacts = useMemo(() => {
    let result = [...customers];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(contact => {
        const matchesName = contact.name?.toLowerCase().includes(searchLower);
        const matchesCompany = contact.company?.toLowerCase().includes(searchLower);
        const matchesEmail = contact.email?.toLowerCase().includes(searchLower);
        const matchesPhone = contact.phone?.toLowerCase().includes(searchLower);
        const matchesContacts = contact.contacts?.some(c => 
          c.name?.toLowerCase().includes(searchLower) ||
          c.email?.toLowerCase().includes(searchLower) ||
          c.phone?.toLowerCase().includes(searchLower)
        );
        return matchesName || matchesCompany || matchesEmail || matchesPhone || matchesContacts;
      });
    }

    // Contact type filter
    if (filters.contactType !== 'all') {
      result = result.filter(contact => contact.contactType === filters.contactType);
    }

    // Active jobs filter
    if (filters.hasActiveJobs !== null) {
      result = result.filter(contact => {
        const jobCounts = getContactJobCounts(contact.id || '');
        return filters.hasActiveJobs ? jobCounts.active > 0 : jobCounts.active === 0;
      });
    }

    // Outstanding invoices filter
    if (filters.hasOutstandingInvoices !== null) {
      result = result.filter(contact => {
        const revenue = getContactRevenue(contact.id || '');
        return filters.hasOutstandingInvoices ? revenue.outstanding > 0 : revenue.outstanding === 0;
      });
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortField) {
        case 'lastContacted':
          comparison = new Date(b.lastContacted || 0).getTime() - new Date(a.lastContacted || 0).getTime();
          break;
        case 'company':
          comparison = (a.company || '').localeCompare(b.company || '');
          break;
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'createdAt':
          comparison = new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
          break;
        case 'contactType': {
          // Centralised sort order — see constants/contactTypes.ts.
          // Bracket scope so the const stays case-local.
          const aOrder = CONTACT_TYPE_SORT_ORDER[a.contactType as keyof typeof CONTACT_TYPE_SORT_ORDER] ?? 99;
          const bOrder = CONTACT_TYPE_SORT_ORDER[b.contactType as keyof typeof CONTACT_TYPE_SORT_ORDER] ?? 99;
          comparison = aOrder - bOrder;
          break;
        }
        case 'revenue':
          const aRevenue = getContactRevenue(a.id || '').total;
          const bRevenue = getContactRevenue(b.id || '').total;
          comparison = bRevenue - aRevenue;
          break;
        case 'recurring': {
          // Most active recurring templates first; ties fall back to name so the
          // long tail of zero-recurring contacts stays alphabetically stable.
          const aRec = recurringByContact[a.id || ''] || 0;
          const bRec = recurringByContact[b.id || ''] || 0;
          comparison = bRec - aRec;
          if (comparison === 0) comparison = (a.company || a.name || '').localeCompare(b.company || b.name || '');
          break;
        }
      }

      return filters.sortDirection === 'asc' ? -comparison : comparison;
    });

    return result;
  }, [customers, filters, getContactJobCounts, getContactRevenue, recurringByContact]);

  const handleSelectContact = (contact: Customer) => {
    setSelectedContact(contact);
  };

  const handleCloseDetail = () => {
    setSelectedContact(null);
  };

  const handleFilterChange = (newFilters: Partial<ClientsFiltersState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
  };

  return (
    <>
      <PageTitle breadcrumbs={contactsBreadCrumbs}>Contacts</PageTitle>
      
      <div className="contacts-page">
        {/* Main Content (top KPI strip removed — straight to filters + list / detail) */}
        <div className="contacts-content">
          {selectedContact ? (
            // Contact Detail View
            <ClientDetailPanel
              client={selectedContact}
              onClose={handleCloseDetail}
              getClientRevenue={getContactRevenue}
              getClientJobCounts={getContactJobCounts}
            />
          ) : (
            // Contact List View
            <>
              <ClientsFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                onNewClient={() => setShowNewContactModal(true)}
                totalCount={customers.length}
                filteredCount={filteredContacts.length}
              />
              <ClientsList
                clients={filteredContacts}
                onSelectClient={handleSelectContact}
                getClientRevenue={getContactRevenue}
                getClientJobCounts={getContactJobCounts}
                recurringByContact={recurringByContact}
                loading={customersLoading}
              />
            </>
          )}
        </div>
      </div>

      {/* New Contact Modal */}
      <NewCustomerModal
        show={showNewContactModal}
        onClose={() => setShowNewContactModal(false)}
        customer={null}
      />
    </>
  );
}

export default ClientsPage;
